import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { sha256 } from '../utils/crypto'

/** License API 基础地址：开发环境指向本地，生产环境走同域反代 */
const LICENSE_API = import.meta.env.VITE_LICENSE_API_URL || '/api/license'

/** deviceId 在 localStorage 的存储 key（vueuse useLocalStorage，字符串会被 JSON 序列化带引号） */
export const DEVICE_ID_STORAGE_KEY = 'license/deviceId'

/** deviceId 最大长度防护（防 localStorage 被篡改注入异常长值） */
const DEVICE_ID_MAX_LEN = 128

/** 设备列表项（与 license-server 返回保持一致） */
export interface DeviceDto {
  id: string
  deviceName: string | null
  createdAt: number
  lastSeenAt: number
  isOnline: boolean
}

export type FetchDevicesResult
  = | { ok: true, devices: DeviceDto[] }
    | { ok: false, error: 'unauthorized' | 'trial_expired' | 'network_error' }

export type UnbindDeviceError
  = | 'unauthorized'
    | 'forbidden'
    | 'cannot_unbind_self'
    | 'trial_expired'
    | 'network_error'
    | 'unbind_failed'

export type UnbindResult = { ok: true } | { ok: false, error: UnbindDeviceError }

/**
 * 同步读取 deviceId。不依赖 pinia/vue 上下文，可在模块作用域调用。
 * - 返回空字符串表示未激活或被篡改
 * - 对异常值做类型 + 长度校验，确保调用方拿到的是可信字符串
 */
export function readDeviceIdSync(): string {
  try {
    const raw = globalThis.localStorage?.getItem(DEVICE_ID_STORAGE_KEY)
    if (!raw)
      return ''
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'string' || parsed.length === 0 || parsed.length > DEVICE_ID_MAX_LEN)
      return ''
    return parsed
  }
  catch {
    return ''
  }
}

export const useLicenseStore = defineStore('license', () => {
  // 持久化状态
  const sessionToken = useLocalStorage<string>('license/sessionToken', '')
  const deviceId = useLocalStorage<string>(DEVICE_ID_STORAGE_KEY, '')
  const deviceName = useLocalStorage<string>('license/deviceName', '')
  const fingerprint = useLocalStorage<string>('license/fingerprint', '')
  const deviceSecret = useLocalStorage<string>('license/deviceSecret', '')
  const activationCodeHash = useLocalStorage<string>('license/activationCodeHash', '')

  // 运行时状态
  const isValidating = ref(false)
  const isActivating = ref(false)
  const error = ref('')
  const licenseInfo = ref<Record<string, unknown> | null>(null)
  const onlineDeviceNames = ref<string[]>([])

  const isActivated = computed(() => !!sessionToken.value)

  // 心跳定时器 & 可见性监听
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let visibilityCleanup: (() => void) | null = null
  let isReauthing = false
  let lastHeartbeatTime = 0

  /** 设置设备指纹（由外部生成后传入） */
  function setFingerprint(fp: string) {
    fingerprint.value = fp
  }

  /** 激活设备 */
  async function activate(code: string, fp: string, name: string): Promise<boolean> {
    isActivating.value = true
    error.value = ''
    fingerprint.value = fp
    try {
      const res = await fetch(`${LICENSE_API}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, fingerprint: fp, deviceName: name }),
      })
      const data = await res.json()
      if (!data.success) {
        error.value = data.error || 'activation_failed'
        return false
      }
      sessionToken.value = data.sessionToken
      deviceId.value = data.deviceId
      deviceName.value = name
      if (data.deviceSecret)
        deviceSecret.value = data.deviceSecret

      // 保存激活码的 hash（用于 WebPush 用户标识）
      activationCodeHash.value = await sha256(code)

      startHeartbeat()
      return true
    }
    catch {
      error.value = 'network_error'
      return false
    }
    finally {
      isActivating.value = false
    }
  }

  /** 验证当前会话是否有效 */
  async function validate(): Promise<boolean> {
    if (!sessionToken.value)
      return false
    isValidating.value = true
    error.value = ''
    try {
      const res = await fetch(`${LICENSE_API}/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken.value}`,
        },
      })
      const data = await res.json()
      if (!data.valid) {
        if (data.error === 'trial_expired') {
          clearSession()
          error.value = 'trial_expired'
          return false
        }
        if (data.error === 'concurrent_limit_exceeded') {
          onlineDeviceNames.value = data.onlineDevices || []
          error.value = 'concurrent_limit_exceeded'
        }
        else {
          // session 过期，尝试用 fingerprint 自动重认证
          if (fingerprint.value && deviceSecret.value) {
            const reauthResult = await reauth()
            if (reauthResult === 'success')
              return true
            if (reauthResult === 'network_error') {
              error.value = 'network_error'
              return false
            }
            // reauth 已经处理过 clearSession + error，避免被下面的 fallback 覆盖
            if (reauthResult === 'device_unbound')
              return false
          }
          clearSession()
          error.value = data.error || 'session_invalid'
        }
        return false
      }
      licenseInfo.value = data.license
      startHeartbeat()
      return true
    }
    catch {
      error.value = 'network_error'
      return false
    }
    finally {
      isValidating.value = false
    }
  }

  type ReauthResult = 'success' | 'auth_failed' | 'network_error' | 'device_unbound'

  /** 用设备指纹 + 设备密钥自动重认证（无需激活码） */
  async function reauth(): Promise<ReauthResult> {
    if (!fingerprint.value || !deviceSecret.value)
      return 'auth_failed'
    try {
      const res = await fetch(`${LICENSE_API}/reauth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fingerprint: fingerprint.value, deviceSecret: deviceSecret.value }),
      })
      const data = await res.json()
      if (!data.success) {
        if (data.error === 'trial_expired') {
          clearSession()
          error.value = 'trial_expired'
        }
        else if (data.error === 'concurrent_limit_exceeded') {
          onlineDeviceNames.value = data.onlineDevices || []
          error.value = 'concurrent_limit_exceeded'
        }
        else if (data.error === 'device_unbound') {
          // 设备被远程解绑：清掉所有设备级凭据，避免下次启动反复触发 reauth
          clearDeviceCredentials()
          clearSession()
          error.value = 'device_unbound'
          return 'device_unbound'
        }
        return 'auth_failed'
      }
      sessionToken.value = data.sessionToken
      deviceId.value = data.deviceId
      startHeartbeat()
      return 'success'
    }
    catch {
      return 'network_error'
    }
  }

  /** 发送心跳 */
  async function heartbeat(): Promise<void> {
    if (!sessionToken.value)
      return
    lastHeartbeatTime = Date.now()
    try {
      const res = await fetch(`${LICENSE_API}/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken.value}` },
      })
      const data = await res.json()
      if (!data.ok) {
        if (data.error === 'license_disabled' || data.error === 'trial_expired') {
          clearSession()
          error.value = data.error
        }
        else if (data.error === 'invalid_token' || data.error === 'unauthorized') {
          if (fingerprint.value && deviceSecret.value && !isReauthing) {
            isReauthing = true
            try {
              const result = await reauth()
              if (result === 'auth_failed')
                clearSession()
              // network_error 时保持现状，等下次心跳重试
            }
            finally {
              isReauthing = false
            }
          }
        }
      }
    }
    catch {
      // 心跳失败静默忽略，可能是临时网络问题
    }
  }

  function startHeartbeat() {
    stopHeartbeat()
    heartbeatTimer = setInterval(heartbeat, 30_000)

    // 标签页切回前台时立即发心跳，防止后台节流导致会话过期
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && sessionToken.value) {
        // 距上次心跳至少 10 秒才发，避免快速切换标签页时密集请求
        if (Date.now() - lastHeartbeatTime > 10_000) {
          heartbeat()
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    visibilityCleanup = () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }

  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
    visibilityCleanup?.()
    visibilityCleanup = null
  }

  function clearSession() {
    sessionToken.value = ''
    deviceId.value = ''
    licenseInfo.value = null
    activationCodeHash.value = ''
    onlineDeviceNames.value = []
    stopHeartbeat()
  }

  /**
   * 清理设备级凭据。仅在「确认设备已被远程解绑或不可恢复」时调用，
   * 否则下次 reauth 会失去自动恢复能力。
   */
  function clearDeviceCredentials() {
    fingerprint.value = ''
    deviceSecret.value = ''
  }

  /** 获取激活码的 hash（供 offlineSync 使用） */
  function getActivationCodeHash(): string {
    return activationCodeHash.value
  }

  /** 获取设备列表 */
  async function fetchDevices(): Promise<FetchDevicesResult> {
    if (!sessionToken.value)
      return { ok: false, error: 'unauthorized' }
    try {
      const res = await fetch(`${LICENSE_API}/devices`, {
        headers: { Authorization: `Bearer ${sessionToken.value}` },
      })
      if (res.status === 401)
        return { ok: false, error: 'unauthorized' }
      if (res.status === 403) {
        const body = await res.json().catch(() => ({}))
        return { ok: false, error: body?.error === 'trial_expired' ? 'trial_expired' : 'unauthorized' }
      }
      if (!res.ok)
        return { ok: false, error: 'network_error' }
      const data = await res.json()
      const list = Array.isArray(data?.devices) ? data.devices as DeviceDto[] : []
      return { ok: true, devices: list }
    }
    catch {
      return { ok: false, error: 'network_error' }
    }
  }

  /** 解绑设备 */
  async function unbindDevice(id: string): Promise<UnbindResult> {
    if (!sessionToken.value)
      return { ok: false, error: 'unauthorized' }
    try {
      const res = await fetch(`${LICENSE_API}/devices/${id}/unbind`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken.value}` },
      })
      const data = await res.json().catch(() => ({}))
      if (data?.success === true)
        return { ok: true }

      const code = typeof data?.error === 'string' ? data.error : ''
      if (code === 'unauthorized' || code === 'invalid_token')
        return { ok: false, error: 'unauthorized' }
      if (code === 'forbidden')
        return { ok: false, error: 'forbidden' }
      if (code === 'cannot_unbind_self')
        return { ok: false, error: 'cannot_unbind_self' }
      if (code === 'trial_expired')
        return { ok: false, error: 'trial_expired' }
      return { ok: false, error: 'unbind_failed' }
    }
    catch {
      return { ok: false, error: 'network_error' }
    }
  }

  return {
    sessionToken,
    deviceId,
    deviceName,
    fingerprint,
    deviceSecret,
    activationCodeHash,
    isActivated,
    isValidating,
    isActivating,
    error,
    licenseInfo,
    onlineDeviceNames,
    activate,
    validate,
    reauth,
    heartbeat,
    startHeartbeat,
    stopHeartbeat,
    clearSession,
    setFingerprint,
    getActivationCodeHash,
    fetchDevices,
    unbindDevice,
  }
})
